import { useEffect, useRef, useState } from "react";
import { Box, Skeleton, Tooltip, IconButton } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import "./MessageBubble.css";
import { useStateValue } from "../../context/global-context-provider";

const TYPING_SPEED_MS = 20;

const MessageBubble = () => {
  const [{ chatData, loading }, dispatch] = useStateValue();
  const containerRef = useRef(null);

  // typing/animation state
  const [animatingIndex, setAnimatingIndex] = useState(null); // index currently "active" (text typing OR voucher revealing)
  const [streamText, setStreamText] = useState("");
  const lastAnimatedIndexRef = useRef(-1);
  const isInitialMountRef = useRef(true);
  const isInitialAnimationRef = useRef(false); // ✅ NEW: Track initial animation

  // image skeleton flag
  const [imageLoaded, setImageLoaded] = useState(false);

  // auto-scroll controls
  const [stickToBottom, setStickToBottom] = useState(true);
  const userInteractingRef = useRef(false);
  const interactTimerRef = useRef(null);

  const isAssistant = (m) => m?.role === "assistant";
  const isVoucher = (m) => m?.messageType === "voucher";
  const isTextAnimatable = (m) =>
    isAssistant(m) && !isVoucher(m) && typeof m?.content === "string";

  // listeners: near-bottom & pause on user interaction
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    const recomputeStick = () => {
      const nearBottom = c.scrollTop + c.clientHeight >= c.scrollHeight - 50;
      setStickToBottom(nearBottom && !userInteractingRef.current);
    };

    const onScroll = () => recomputeStick();
    const onInteract = () => {
      userInteractingRef.current = true;
      setStickToBottom(false);
      if (interactTimerRef.current) clearTimeout(interactTimerRef.current);
      interactTimerRef.current = setTimeout(() => {
        userInteractingRef.current = false;
        recomputeStick();
      }, 600);
    };

    c.addEventListener("scroll", onScroll, { passive: true });
    c.addEventListener("wheel", onInteract, { passive: true });
    c.addEventListener("touchstart", onInteract, { passive: true });
    c.addEventListener("mousedown", onInteract);
    recomputeStick();

    return () => {
      c.removeEventListener("scroll", onScroll);
      c.removeEventListener("wheel", onInteract);
      c.removeEventListener("touchstart", onInteract);
      c.removeEventListener("mousedown", onInteract);
      if (interactTimerRef.current) clearTimeout(interactTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setImageLoaded(true);
  }, [chatData, streamText, animatingIndex]);

  // initial mount: jump to bottom
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    if (isInitialMountRef.current && Array.isArray(chatData) && chatData.length > 0) {
      const jump = () => { c.scrollTop = c.scrollHeight; };
      requestAnimationFrame(jump);
      setTimeout(jump, 0);
      setTimeout(jump, 250);
    }
  }, [chatData?.length]);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    if (isInitialMountRef.current && imageLoaded) {
      c.scrollTop = c.scrollHeight;
    }
  }, [imageLoaded]);

  // scroll on new messages
  useEffect(() => {
    const c = containerRef.current;
    if (!c || !Array.isArray(chatData) || chatData.length === 0) return;
    const lastMsg = chatData[chatData.length - 1];
    if (lastMsg?.role === "user") {
      c.scrollTop = c.scrollHeight; // always jump for user send
    } else if (stickToBottom) {
      c.scrollTo({ top: c.scrollHeight, behavior: "smooth" });
    }
  }, [chatData, stickToBottom]);

  // nudge while typing if we stick
  useEffect(() => {
    const c = containerRef.current;
    if (c && stickToBottom && animatingIndex !== null) {
      c.scrollTop = c.scrollHeight;
    }
  }, [streamText, animatingIndex, stickToBottom]);

  // ✅ MODIFIED: sequencing (include vouchers in sequence)
  useEffect(() => {
    if (!Array.isArray(chatData) || chatData.length === 0) {
      // Only set false if not in initial animation
      if (!isInitialAnimationRef.current) {
        dispatch({ type: "SET_ASSISTANT_TYPING", value: false });
      }
      return;
    }

    const assistantIndices = chatData
      .map((m, i) => (isAssistant(m) ? i : null))
      .filter((i) => i !== null);

    if (assistantIndices.length === 0) {
      // Only set false if not in initial animation
      if (!isInitialAnimationRef.current) {
        dispatch({ type: "SET_ASSISTANT_TYPING", value: false });
      }
      return;
    }

    if (isInitialMountRef.current) {
      isInitialAnimationRef.current = true; // ✅ Mark that we're doing initial animation
      
      // On initial mount, start from first assistant message if no user messages yet
      const hasAnyUserMessage = chatData.some((m) => m?.role === "user");

      if (!hasAnyUserMessage) {
        const firstIdx = assistantIndices[0];
        lastAnimatedIndexRef.current = firstIdx - 1; // nothing shown yet
        startSequence(firstIdx);
      } else {
        const lastIdx = assistantIndices[assistantIndices.length - 1];
        lastAnimatedIndexRef.current = lastIdx - 1; // earlier assistant msgs show instantly
        startSequence(lastIdx);
      }
      isInitialMountRef.current = false;
      return;
    }

    if (animatingIndex === null) {
      const nextIdx = assistantIndices.find((i) => i > lastAnimatedIndexRef.current);
      if (nextIdx !== undefined && nextIdx !== null) {
        startSequence(nextIdx);
      } else {
        dispatch({ type: "SET_ASSISTANT_TYPING", value: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatData]);

  const startSequence = (index) => {
    const msg = chatData?.[index];
    if (!isAssistant(msg)) return;

    dispatch({ type: "SET_ASSISTANT_TYPING", value: true });
    setAnimatingIndex(index);

    if (isVoucher(msg)) {
      // Reveal voucher immediately on its turn (optionally tiny delay for feel)
      setTimeout(() => {
        lastAnimatedIndexRef.current = index;
        setAnimatingIndex(null);
        isInitialAnimationRef.current = false; // ✅ Clear flag when done
        
        // queue next assistant message
        const nextIdx = chatData
          ?.map((m, ii) => (isAssistant(m) && ii > lastAnimatedIndexRef.current ? ii : null))
          .find((ii) => ii !== null);
        if (nextIdx !== undefined && nextIdx !== null) {
          setTimeout(() => startSequence(nextIdx), 40);
        } else {
          dispatch({ type: "SET_ASSISTANT_TYPING", value: false });
        }
      }, 60);
      return;
    }

    // Text message → type it out
    setStreamText("");
    const text = msg.content || "";
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setStreamText(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        lastAnimatedIndexRef.current = index;
        setAnimatingIndex(null);
        isInitialAnimationRef.current = false; // ✅ Clear flag when animation finishes

        const nextIdx = chatData
          ?.map((m, ii) => (isAssistant(m) && ii > lastAnimatedIndexRef.current ? ii : null))
          .find((ii) => ii !== null);

        if (nextIdx !== undefined && nextIdx !== null) {
          setTimeout(() => startSequence(nextIdx), 40);
        } else {
          dispatch({ type: "SET_ASSISTANT_TYPING", value: false });
        }
      }
    }, TYPING_SPEED_MS);
  };

  // ✅ MODIFIED: safety: ensure typing flag drops if nothing animating (but not during initial animation)
  useEffect(() => {
    if (animatingIndex === null && !isInitialAnimationRef.current) {
      dispatch({ type: "SET_ASSISTANT_TYPING", value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animatingIndex]);

  const isAppleDevice = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

  const downloadImage = async (src) => {
    try {
      const link = document.createElement("a");
      link.href = src;
      link.download = "bar-voucher.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download image", error);
    }
  };

  const renderContent = (msg, i) => {
    // Hide ANY assistant message (voucher or text) until its turn
    if (isAssistant(msg)) {
      if (i === animatingIndex && isTextAnimatable(msg)) {
        return streamText; // typing
      }
      if (i > lastAnimatedIndexRef.current && i !== animatingIndex) {
        return ""; // queued → not revealed yet
      }
    }

    // Voucher render (when revealed or its turn)
    if (isVoucher(msg)) {
      return imageLoaded ? (
        <Box
          component="img"
          src={msg.metadata?.imageUrl || ""}
          alt="coupon-image"
          sx={{ maxWidth: "100%", height: "100%", borderRadius: 2 }}
        />
      ) : (
        <Skeleton
          animation="wave"
          variant="rectangular"
          width={500}
          height={400}
          sx={{ borderRadius: 4 }}
        />
      );
    }

    // Assistant text (already revealed) or user/other
    if (isAssistant(msg) && isTextAnimatable(msg)) {
      if (i <= lastAnimatedIndexRef.current && i !== animatingIndex) {
        return msg.content;
      }
    }

    // User or anything else
    return msg.content;
  };

  return (
    <Box ref={containerRef} className="chat-container">
      {chatData?.map((msg, i) => (
        <Box
          key={i}
          className={`bubble ${msg.role} ${isVoucher(msg) ? "image" : ""}`}
          sx={{
            fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
            lineHeight: 1.4,
            wordBreak: "break-word",
            whiteSpace: "pre-line",
          }}
        >
          {renderContent(msg, i)}

          {isVoucher(msg) && !isAppleDevice() ? (
            <Box className="image-actions">
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={() => downloadImage(msg.metadata?.imageUrl || "")}
                >
                  <DownloadRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
      ))}

      {loading && (
        <Box className={`bubble assistant`}>
          <div className="typing-dots">
            <span></span><span></span><span></span>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default MessageBubble;